import {
  Link,
  LoaderFunction,
  Navigate,
  useLoaderData,
  useParams,
} from 'react-router-dom';
import {
  MailOpenIcon,
  AnnotationIcon,
  ShieldCheckIcon,
} from '@heroicons/react/outline';
import { paths } from '@mosey/api-types';
import { Button } from '@mosey/components/buttons/Button';
import {
  useTodoTasks,
  useResolverUrl,
  useTasksFrameworkLocation,
} from '../utils/hooks';
import confetti from '../../../assets/tasks-framework/task-resolver-interstitial-confetti.svg';
import { ResolverType, ResolverRouteParams } from '../utils/types';
import { ResolverSidebar } from './Sidebar';
import { FullHeightWrapper, Interstitial } from '../common/Interstitial';
import { NextButton } from '../common/NextButton';
import { getFinishRedirectUrl } from '../utils/session';
import { api } from '../../../utils/fetchApi';
import { MutedStateSeal } from '../common/MutedStateSeal';
import {
  generateResolverTasksAPIUrl,
  generateResolverTasksBrowserUrl,
} from '../utils';
import { Animation } from '@mosey/components/animation/Animation';

const stateSetupCompleteBullets = [
  {
    title: 'Weekly Summary',
    description:
      'You will receive a weekly summary email that includes any upcoming tasks',
    Icon: MailOpenIcon,
  },
  {
    title: 'Threshold Notifications',
    description:
      'You will be notified when you hit thresholds such as employee counts or payroll amounts that require further action',
    Icon: AnnotationIcon,
  },
  {
    title: '24/7 Monitoring',
    description:
      'Ongoing monitoring of upcoming laws and changes in requirements ensure that you will always be compliant',
    Icon: ShieldCheckIcon,
  },
];

export const loader: LoaderFunction = async ({
  params: { locationId, resolverType },
}) => {
  if (
    resolverType !== ResolverType.Assessment &&
    resolverType !== ResolverType.Question &&
    resolverType !== ResolverType.Review
  ) {
    return false;
  }

  const finishRedirectUrl = getFinishRedirectUrl();

  if (finishRedirectUrl) {
    /**
     * If we have a finishRedirectUrl, we know exactly where we want to redirect
     * to so we don't need to fetch tasks below to determine if there's a
     * resolver that needs attention.
     */
    return false;
  }

  const response = await api(
    generateResolverTasksAPIUrl(
      resolverType === ResolverType.Assessment
        ? ResolverType.Setup
        : ResolverType.Todo,
      locationId,
    ),
  );
  const newRequirementTasks =
    (await response.json()) as paths['/api/compliance/tasks']['get']['responses'][200]['content']['application/json'];

  return newRequirementTasks.filter((t) => t.status === 'todo').length > 0;
};

const StateSetupComplete = () => {
  const location = useTasksFrameworkLocation()!;

  return (
    <FullHeightWrapper>
      <Interstitial
        percentComplete={100}
        title={
          <>
            You’re all set up in{' '}
            <span className="text-teal-800">{location.name}</span>!
          </>
        }
        description="You’ve activated monitoring and alerting for state and local compliance."
        rightHandContent={
          <div
            className="bg-no-repeat py-8 sm:w-[512px] sm:bg-[center_top_90px] sm:pb-0 sm:pt-[135px]"
            style={{ backgroundImage: `url(${confetti})` }}
          >
            <div className="mx-auto size-[320px]">
              <MutedStateSeal />
            </div>
          </div>
        }
        actions={
          <NextButton to={`/locations/usa/${location.code}`}>Finish</NextButton>
        }
      >
        <ul className="max-w-xl space-y-10 text-zinc-700">
          {stateSetupCompleteBullets.map(({ title, description, Icon }) => (
            <li key={title} className="flex gap-x-3">
              <Icon
                className="mt-1 size-6 shrink-0 text-teal-700"
                aria-hidden
              />
              <div>
                <p className="text-2xl font-semibold text-teal-900">{title}</p>
                <p>{description}</p>
              </div>
            </li>
          ))}
        </ul>
      </Interstitial>
    </FullHeightWrapper>
  );
};

const QuestionsComplete = () => {
  const { resolverType } = useParams<ResolverRouteParams>();
  const finishRedirectUrl = getFinishRedirectUrl();
  const location = useTasksFrameworkLocation();
  const hasNewTasks = useLoaderData() as boolean;
  let continueUrl = '/home';

  if (finishRedirectUrl) {
    continueUrl = finishRedirectUrl;
  } else if (location) {
    if (resolverType === ResolverType.Assessment && hasNewTasks) {
      continueUrl = generateResolverTasksBrowserUrl({
        locationId: location.code,
        resolverType: ResolverType.Setup,
      });
    } else {
      continueUrl = `/locations/usa/${location.code}`;
    }
  }

  let headerText = 'You’re done answering questions!';
  if (location) {
    headerText = `You’ve configured ${location.name}!`;
  }

  let descriptionText;
  if (hasNewTasks) {
    descriptionText = `Your answers have generated ${resolverType === ResolverType.Assessment ? 'setup' : ''} tasks tailored to your situation.`;
  } else {
    descriptionText = 'There’s no action required based on your answers.';
  }

  return (
    <>
      <div className="mb-4 max-w-xl space-y-6 pt-6 text-center">
        <div className="mx-auto">
          <Animation
            loadAnimation={() =>
              import(
                '../../../assets/animations/tasks-framework/resolver-complete-question.json'
              )
            }
            className="max-w-X aspect-[2/1] w-full"
          />
        </div>
        <div className="mb-8 space-y-1">
          <h2 className="text-4xl font-bold tracking-tight">{headerText}</h2>
          {!finishRedirectUrl && (
            <p className="text-zinc-800">{descriptionText}</p>
          )}
        </div>
      </div>

      <Button size="xlarge" as={Link} to={continueUrl}>
        Continue
      </Button>
    </>
  );
};

const RequirementsComplete = () => {
  const { resolverType } = useParams<ResolverRouteParams>();
  const location = useTasksFrameworkLocation();
  let finishRedirectUrl = getFinishRedirectUrl();

  if (location && !finishRedirectUrl) {
    finishRedirectUrl = `/locations/usa/${location.code}`;
  }

  let headerText = 'Great Job!';
  if (resolverType === ResolverType.Overdue) {
    headerText = `You’re all caught up!`;
  }

  // Default description text that should never be shown
  let descriptionText = 'That’s the end of this list.';
  if (resolverType === ResolverType.Overdue) {
    descriptionText = `You’ve completed all overdue tasks${location ? ` for ${location.name}` : ''}.`;
  } else if (resolverType === ResolverType.Todo) {
    descriptionText = `You’ve completed all outstanding tasks${location ? ` for ${location.name}` : ''}.`;
  }

  return (
    <>
      <div className="mx-auto">
        <Animation
          loadAnimation={() =>
            import(
              '../../../assets/animations/tasks-framework/resolver-complete-requirement.json'
            )
          }
          className="max-w-X aspect-[2/1] w-full"
        />
      </div>
      <div className="mb-6 max-w-xl space-y-1 text-center">
        <h2 className="text-4xl font-bold tracking-tight">{headerText}</h2>
        <p className="text-zinc-800">{descriptionText}</p>
      </div>

      <Button size="xlarge" as={Link} to={finishRedirectUrl ?? '/home'}>
        Finish
      </Button>
    </>
  );
};

const HandbookComplete = () => {
  const location = useTasksFrameworkLocation();
  let finishRedirectUrl = getFinishRedirectUrl();

  if (location && !finishRedirectUrl) {
    finishRedirectUrl = `/locations/usa/${location.code}`;
  }

  const headerText = 'Great Job!';
  const descriptionText = `You’ve adopted all required policies${location ? ` for ${location.name}` : ''}.`;

  return (
    <>
      <div className="mx-auto">
        <Animation
          loadAnimation={() =>
            import(
              '../../../assets/animations/tasks-framework/resolver-complete-handbook.json'
            )
          }
          className="max-w-X aspect-[2/1] w-full"
        />
      </div>
      <div className="mb-6 max-w-xl space-y-1 text-center">
        <h2 className="text-4xl font-bold tracking-tight">{headerText}</h2>
        <p className="text-zinc-800">{descriptionText}</p>
      </div>

      <Button size="xlarge" as={Link} to={finishRedirectUrl ?? '/home'}>
        Finish
      </Button>
    </>
  );
};

export const Component = () => {
  const { resolverType } = useParams<ResolverRouteParams>();
  const todoTasks = useTodoTasks();
  const resolverUrl = useResolverUrl();

  if (todoTasks.length > 0) {
    return <Navigate to={resolverUrl || '/home'} replace />;
  }

  if (resolverType === ResolverType.Setup) {
    return <StateSetupComplete />;
  }

  return (
    <div className="flex h-full max-w-screen-2xl @container/task">
      <ResolverSidebar />

      <section className="mb-24 flex size-full flex-col items-center justify-center gap-y-6 self-center overflow-y-auto">
        {resolverType === ResolverType.Assessment ||
        resolverType === ResolverType.Question ||
        resolverType === ResolverType.Review ? (
          <QuestionsComplete />
        ) : resolverType === ResolverType.Handbook ? (
          <HandbookComplete />
        ) : (
          <RequirementsComplete />
        )}
      </section>
    </div>
  );
};
